import { useState, useEffect, createContext, useContext } from "react";
import { Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import crm, { apiErr } from "./crmApi";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import { ForgotPassword, ResetPassword } from "./CrmAuth";
import Shell from "./CrmShell";
import "./crm.css";

const Ctx = createContext(null);
export const useCrm = () => useContext(Ctx);
const money = (n) => n == null ? "—" : (n >= 10000000 ? `₹${(n/10000000).toFixed(1)}Cr` : n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${n}`);

export default function CrmApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!localStorage.getItem("crm_token")) { setLoading(false); return; }
    crm.me().then(setUser).catch(() => localStorage.removeItem("crm_token")).finally(() => setLoading(false));
  }, []);
  const logout = () => { localStorage.removeItem("crm_token"); setUser(null); };
  if (loading) return <div className="crm-root min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#236B45]" /></div>;
  return (
    <Ctx.Provider value={{ user, setUser, logout }}>
      <div className="crm-root min-h-screen">
        <Routes>
          <Route path="login" element={user ? <Navigate to="/crm" /> : <Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="*" element={user ? <Shell /> : <Navigate to="/crm/login" />} />
        </Routes>
      </div>
    </Ctx.Provider>
  );
}

function Login() {
  const { setUser } = useCrm();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { token } = await crm.login(email, password); localStorage.setItem("crm_token", token); setUser(await crm.me()); nav("/crm"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <div className="hidden lg:flex flex-col justify-between bg-[#236B45] text-white p-12">
        <div className="flex items-center gap-3">
          <LogoMark dark className="h-10 w-10 shrink-0" />
          <p className="text-xl font-bold"><span>PlotBy</span><span className="text-[#C9A45C]">Square</span> <span className="text-white/70 font-medium">CRM</span></p>
        </div>
        <div>
          <p className="text-3xl font-bold leading-tight max-w-md">Every lead, follow-up and call — in one workspace.</p>
          <p className="mt-3 text-white/70 max-w-md text-[15px]">Sales operations for PlotBySquare channel partners: leads, tasks, calling activity, team performance, HR and website listings.</p>
        </div>
        <p className="text-[11px] font-semibold tracking-[0.3em] text-white/50">REAL ESTATE. HIGHER POSSIBILITIES.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8"><LogoMark className="h-8 w-8" /><span className="font-bold text-lg"><span className="text-[#236B45]">PlotBy</span><span className="text-[#C9A45C]">Square</span> CRM</span></div>
          <h2 className="text-2xl font-bold text-[#25352C]">Sign in</h2>
          <p className="text-sm text-[#5F6E64] mt-1">Use your employee credentials.</p>
          <label className="crm-label mt-6" htmlFor="crm-email">Work email</label>
          <input id="crm-email" type="email" required autoComplete="username" data-testid="crm-email" value={email} onChange={(e) => setEmail(e.target.value)} className="crm-in !h-10" placeholder="name@company.com" />
          <div className="flex items-center justify-between mt-4"><label className="crm-label !mb-0" htmlFor="crm-password">Password</label><Link to="/crm/forgot-password" data-testid="forgot-link" className="text-xs font-semibold text-[#236B45] hover:underline">Forgot password?</Link></div>
          <input id="crm-password" type="password" required autoComplete="current-password" data-testid="crm-password" value={password} onChange={(e) => setPassword(e.target.value)} className="crm-in !h-10 mt-1.5" />
          <button data-testid="crm-login-btn" disabled={busy} className="crm-btn crm-btn-primary w-full !h-10 mt-6">{busy && <Loader2 size={16} className="animate-spin" />} Sign in</button>
        </form>
      </div>
    </div>
  );
}

export function Loader() { return <div className="py-20 flex justify-center" data-testid="crm-loader"><Loader2 className="animate-spin text-[#236B45]" /></div>; }
export { money };
